import {DialogFragment, PageFragment, PageFragmentProps, PageFragmentState} from "shared/PageFragment";
import {ReactElement} from "react";
import {FormGen} from "shared/formgen";
import {Box, Button, Typography} from "@mui/material";
import {DW_LG, PAGE_FRAGMENT_HALF_WIDTH, PD_LG, PD_MD, SZ_LG, SZ_XXXLG} from "shared/dimens";
import {FormGenContainer} from "shared/FormGenContainer";
import {AvatarImage} from "../util/Components";
import {StyledBoxColumn, StyledBoxRow, StyledHorizontalDivider} from "../../shared/StyledComponents";
import {ChatPersona} from "../../toktiv-common/types";
import callAnim from "./res/anim/call.svg";

enum ChatType {
  CHAT,
  CALL,
}

export type NewAppChatFragmentProps = PageFragmentProps & {
  persona: ChatPersona,
  onCreateNewAppChat: (newAppChat: NewAppChat) => void,
  onCreateNewAppCall: () => void,
}

type NewAppChatFragmentState = PageFragmentState & {
  chatType: ChatType,
}

export class NewAppChat {

  @FormGen({type: "text", size: "xsmall", name: "Say something", required: true})
  message: string = "Hello";
}

function $_(text: string): string {
  return text.substring(0, 1).toLowerCase() + text.substring(1);
}

export class NewAppChatFragment extends DialogFragment<NewAppChatFragmentProps, NewAppChatFragmentState> {

  private readonly newAppChat = new NewAppChat();

  protected onCreateState(): NewAppChatFragmentState {
    return {
      ...super.onCreateState(),
      chatType: ChatType.CHAT,
      title: "New chat / call",
    };
  }

  protected styleFlags(): number {
    return PageFragment.STYLE_ACTION_PANEL_TYPE_BUTTON_FLAG;
  }

  protected getActionButtonText(): string {
    switch (this.state.chatType) {
      default:
      case ChatType.CHAT:
        return "Start chat";
      case ChatType.CALL:
        return "Start call";
    }
  }

  onActionButtonClicked() {
    switch (this.state.chatType) {
      default:
      case ChatType.CHAT:
        this.props.onCreateNewAppChat(this.newAppChat);
        break;
      case ChatType.CALL:
        this.props.onCreateNewAppCall();
        break;
    }
  }

  renderContent(): ReactElement {
    const persona = this.props.persona;
    const interests = `${persona.interests.slice(0, persona.interests.length - 1).map(t => $_(t)).join(", ")} and ${$_(persona.interests[persona.interests.length - 1])}`;
    const bio = `Hi, my name is ${persona.displayName}. I am ${$_(persona.background)} and I am ${$_(persona.personality)} My interests are in ${interests}. I am ${$_(persona.languageRole)}`;
    return <Box style={{display: "flex", flexDirection: "column", width: "84vw", maxWidth: DW_LG, gap: PD_MD}}>
      <StyledBoxColumn style={{alignItems: "center", padding: PD_LG}}>
        <AvatarImage
          persona={persona}
          flip={true}
          style={{width: "33%", maxWidth: PAGE_FRAGMENT_HALF_WIDTH}}/>
        <Typography variant="h4">{persona.displayName}</Typography>
        <Typography style={{textAlign: "center"}}>{bio}</Typography>
      </StyledBoxColumn>
      <StyledHorizontalDivider/>
      <StyledBoxColumn style={{gap: 0}}>
        <StyledBoxRow style={{padding: PD_MD, justifyContent: "center"}}>
          <Button
            onClick={() => this.setState({chatType: ChatType.CHAT})}
            variant={this.state.chatType === ChatType.CHAT ? "contained" : null}>Chat</Button>
          <Button
            onClick={() => this.setState({chatType: ChatType.CALL})}
            variant={this.state.chatType === ChatType.CALL ? "contained" : null}>Call</Button>
        </StyledBoxRow>
        <StyledBoxColumn style={{height: SZ_XXXLG, justifyContent: "center"}}>
          {this.state.chatType === ChatType.CHAT
            ? <FormGenContainer
              content={this.newAppChat}
              onContainerRequiredItemsFilledChanged={(container, filled) => this.setActionButtonEnabled(filled)}/>
            : null}
          {this.state.chatType === ChatType.CALL
            ? <img src={callAnim} style={{width: SZ_LG, height: SZ_LG, alignSelf: "center"}}/>
            : null}
        </StyledBoxColumn>
      </StyledBoxColumn>
    </Box>;
  }
}
